import { getCSSValue, getUniqueKey, deleteUniqueKeyName } from '../../utils/core'
import { PSEUDO_STYLE_MARKER, DIR_DEFER_ATTR } from './markers'

function getRuleMap(myWindow: Window): any {
  myWindow.document
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')
    .forEach((linkElem) => {
      if (linkElem.sheet == null) {
        // 방어코드 - styleToInlineStyle 적용 시점까지 로딩되지 않은 style은 나중에 로딩되면 문제를 발생시킬 가능성이 크기 떄문에 차단하도록 한다.
        console.log('link[rel="stylesheet"][href] sheet is not loaded : ', linkElem.href)
        linkElem.disabled = true
      } else if (linkElem.sheet.ownerNode == null) {
        // 방어코드 - sheet.ownerNode가 null인 경우는 로딩이 되었어도 stylesheet에 반영되지 않은 경우이므로 차단한다.
        linkElem.disabled = true
      }
    })

  const sheets = myWindow.document.styleSheets

  const roleMap = {}

  const applyStyleRule = (styleRule: CSSStyleRule) => {
    const elementList = myWindow.document.querySelectorAll(styleRule.selectorText)
    elementList.forEach((element) => {
      const niElement = element as any
      if (niElement.niUniqueKey == null) {
        niElement.niUniqueKey = getUniqueKey('styleToInline')
      }
      if (roleMap[niElement.niUniqueKey] == null) {
        roleMap[niElement.niUniqueKey] = []
      }
      roleMap[niElement.niUniqueKey].push(styleRule)
    })
  }

  Array.from(sheets).forEach((sheet) => {
    let isAccessible = true
    if (sheet.href != null && sheet.href.length > 0) {
      //external css인 경우.
      try {
        sheet.cssRules
        isAccessible = true
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
      } catch (e: any) {
        // console.log(' --- access error : ', e)
        isAccessible = false
      }
    }

    if (isAccessible === true) {
      const rules = Array.from(sheet.cssRules)
      rules.forEach((rule /* , idx */) => {
        const ruleType = rule.constructor.name
        if (ruleType === 'CSSStyleRule') {
          const styleRule = rule as CSSStyleRule
          applyStyleRule(styleRule)
        } else if (ruleType === 'CSSMediaRule') {
          let mediaRule = rule as CSSMediaRule
          // console.info(' --- mediaquery : ', mediaRule.conditionText)
          if (myWindow.matchMedia(mediaRule.conditionText).matches === true) {
            const mediaCssRules = Array.from(mediaRule.cssRules)
            mediaCssRules.forEach((mediaRule) => {
              if (mediaRule.constructor.name === 'CSSStyleRule') {
                const styleRule = mediaRule as CSSStyleRule
                applyStyleRule(styleRule)
              }
            })
          }
        } else if (ruleType === 'CSSUnknownRule') {
          console.log(' --- unknown rule : ', rule.constructor.name)
        }
      })

      // 외부 CSS(<link>)는 규칙을 위에서 인라인화로 흡수했으므로, 원본 시트를 disable 한다.
      // disable 하지 않으면 이 <link> 가 cloneNode/outerHTML 로 결과 본문에 살아남아, 소비 컨텍스트
      // (메인 문서 .htmlMailBody)에 재주입될 때 전역 스타일시트로 다시 로드되어 인라인 스타일과
      // 중복 적용되고 앱 전역 UI 를 오염시킨다. disabled 는 content attribute 로 반영돼 클론·직렬화에
      // 그대로 따라가므로(접근 불가 시트를 처리하는 아래 else 분기와 동일한 메커니즘) 재주입 시 로드되지 않는다.
      if (sheet.href != null && sheet.href.length > 0) {
        const elem = sheet.ownerNode as HTMLLinkElement
        elem.disabled = true
      }
    } else {
      console.log(' --- inaccessible external sheet : ', sheet.href)

      const elem = sheet.ownerNode as HTMLLinkElement
      elem.disabled = true
    }
  })

  return roleMap
}

function matchRules(el: Element, ruleMap: any): CSSStyleRule[] {
  let ret = []

  const niElement = el as any
  const key = niElement.niUniqueKey

  if (key != null && ruleMap[key] != null) {
    const rules = ruleMap[key]
    rules.forEach((rule) => {
      ret.push(rule)
    })
  }

  sortBySpecificity(el, ret)

  return ret
}

function _find(string, re) {
  var matches = string.match(re)
  return matches ? matches.length : 0
}

const ELEMENT_RE = /[\w-]+/g
const ID_RE = /#[\w-]+/g
const CLASS_RE = /\.[\w-]+/g
const ATTR_RE = /\[[^\]]+\]/g
// :not() pseudo-class does not add to specificity, but its content does as if it was outside it
const PSEUDO_CLASSES_RE = /\:(?!not)[\w-]+(\(.*\))?/g
const PSEUDO_ELEMENTS_RE = /\:\:?(after|before|first-letter|first-line|selection)/g

// calculates the specificity of a given `selector`
function calculateScore(selector) {
  var score = [0, 0, 0],
    parts = selector.split(' '),
    part,
    match
  //TODO: clean the ':not' part since the last ELEMENT_RE will pick it up
  while (((part = parts.shift()), typeof part == 'string')) {
    // find all pseudo-elements
    match = _find(part, PSEUDO_ELEMENTS_RE)
    score[2] += match
    // and remove them
    match && (part = part.replace(PSEUDO_ELEMENTS_RE, ''))
    // find all pseudo-classes
    match = _find(part, PSEUDO_CLASSES_RE)
    score[1] += match
    // and remove them
    match && (part = part.replace(PSEUDO_CLASSES_RE, ''))
    // find all attributes
    match = _find(part, ATTR_RE)
    score[1] += match
    // and remove them
    match && (part = part.replace(ATTR_RE, ''))
    // find all IDs
    match = _find(part, ID_RE)
    score[0] += match
    // and remove them
    match && (part = part.replace(ID_RE, ''))
    // find all classes
    match = _find(part, CLASS_RE)
    score[1] += match
    // and remove them
    match && (part = part.replace(CLASS_RE, ''))
    // find all elements
    score[2] += _find(part, ELEMENT_RE)
  }
  return parseInt(score.join(''), 10)
}

// returns the heights possible specificity score an element can get from a give rule's selectorText
function getSpecificityScore(element, selector_text) {
  var selectors = selector_text.split(','),
    selector,
    score,
    result = 0
  while ((selector = selectors.shift())) {
    if (element.matches(selector) === true) {
      score = calculateScore(selector)
      result = score > result ? score : result
    }
  }
  return result
}

function sortBySpecificity(element, rules) {
  // comparing function that sorts CSSStyleRules according to specificity of their `selectorText`
  function compareSpecificity(a, b) {
    return (
      getSpecificityScore(element, b.selectorText) - getSpecificityScore(element, a.selectorText)
    )
  }
  return rules.sort(compareSpecificity)
}

function cloneElem(element: HTMLElement | SVGElement): HTMLElement | SVGElement {
  if (element.tagName === 'BODY') {
    const newDiv = document.createElement('DIV')
    let attr
    let attributes = Array.from(element.attributes)
    try {
      while ((attr = attributes.pop())) {
        newDiv.setAttribute(attr.nodeName, attr.nodeValue)
      }
    } catch (e) {
      console.log(' --- cloneElem error : ', e)
    }
    return newDiv
  }

  return element.cloneNode(false) as HTMLElement
}

interface CheckResult {
  prop: string
  value: any
  isImportant: boolean
}

// 셀렉터에 의사요소(::before/::after 등)가 포함됐는지 판별 (비-global — test lastIndex 부작용 없음)
const PSEUDO_ELEMENT_MATCH = /::?(?:before|after|first-letter|first-line|selection)\b/i

interface PseudoDecl {
  value: string
  important: boolean
}

/**
 * 의사요소 규칙의 셀렉터를 뷰어 출력 형태에 맞게 재작성한다.
 * - class/id 토큰에 prefix 부여(styleToInlineStyle 의 clsPrefix/idPrefix 와 동일 규칙)
 * - 단일콜론 의사요소를 `::` 표준형으로 정규화(dedup 키 일관)
 *
 * 컨테이너 스코프(`[data-email-id] .htmlMailBody` / `.print-body`)는 여기서 붙이지 않는다.
 * 결과 셀렉터는 `blockquote.x_blockquote-quote::before` 처럼 **클래스 한정**이라 인용문/타 메일을
 * 오염시키지 않으며(idempotent), 스코프는 소비 컨텍스트(뷰어/print)에서 필요 시 prepend 한다.
 */
export function rewritePseudoSelector(
  selector: string,
  clsPrefix: string,
  idPrefix: string,
): string {
  let s = selector.trim().replace(/\s+/g, ' ')
  s = s.replace(/::?(before|after|first-letter|first-line|selection)\b/gi, '::$1')
  if (clsPrefix.length > 0) {
    s = s.replace(/\.(-?[_a-zA-Z][\w-]*)/g, (_m, name) => `.${clsPrefix}${name}`)
  }
  if (idPrefix.length > 0) {
    s = s.replace(/#(-?[_a-zA-Z][\w-]*)/g, (_m, name) => `#${idPrefix}${name}`)
  }
  return s
}

/**
 * 주입된 의사요소 `<style>`(marker 로 식별) 내용을 `@scope (<scopeRoot>) { … }` 로 감싸 적용 범위를
 * 한정한다. getSafeHTML 은 스코프 없이(클래스 한정) 의사요소 `<style>` 를 남기고, 소비 컨텍스트가
 * 이 함수로 스코프를 부여한다 — 뷰어는 `[data-email-id="{id}"] .htmlMailBody`, print 는 `.print-body`.
 *
 * 이미 `@scope (...)` 로 감싸진 경우(뷰어에서 감싼 것을 print 가 clone 한 경우 등)에는 scope root 만
 * 교체한다(재래핑 방지 + print 재스코프 동시 처리). 오류/빈 내용은 무해하게 건너뛴다.
 */
export function applyPseudoStyleScope(
  root: ParentNode,
  scopeRoot: string,
  marker = PSEUDO_STYLE_MARKER,
): void {
  root.querySelectorAll(`style[${marker}]`).forEach((styleEl) => {
    const css = styleEl.textContent ?? ''
    if (css.length === 0) return
    styleEl.textContent = /^\s*@scope\b/.test(css)
      ? css.replace(/@scope\s*\([^)]*\)/, `@scope (${scopeRoot})`)
      : `@scope (${scopeRoot}) {\n${css}\n}`
  })
}

/**
 * 문서의 모든 스타일시트에서 의사요소 규칙만 수집→셀렉터 재작성→동일 셀렉터 병합한 CSS 문자열 반환.
 * styleToInlineStyle 이 인라인화하지 못하는 `::before`/`::after` 장식을 뷰어에 보존하기 위한 것.
 * 순수 additive — 어떤 오류(cross-origin 시트, 파싱 실패 등)든 해당 규칙/시트만 건너뛴다.
 */
export function collectPseudoElementCSS(
  win: Window,
  clsPrefix: string,
  idPrefix: string,
  skipMarker: string,
): string {
  const merged = new Map<string, Map<string, PseudoDecl>>()
  const order: string[] = []

  const takeRule = (rule: CSSStyleRule): void => {
    const selectorText = rule.selectorText
    if (selectorText == null || PSEUDO_ELEMENT_MATCH.test(selectorText) === false) return
    const parts = selectorText
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0 && PSEUDO_ELEMENT_MATCH.test(p))
    if (parts.length === 0) return
    const style = rule.style
    for (const part of parts) {
      const sel = rewritePseudoSelector(part, clsPrefix, idPrefix)
      let decl = merged.get(sel)
      if (decl == null) {
        decl = new Map<string, PseudoDecl>()
        merged.set(sel, decl)
        order.push(sel)
      }
      for (const prop of Array.from(style)) {
        const important = style.getPropertyPriority(prop) === 'important'
        const prev = decl.get(prop)
        if (important === true || prev == null || prev.important === false) {
          decl.set(prop, { value: style.getPropertyValue(prop), important })
        }
      }
    }
  }

  const walk = (rules: CSSRuleList): void => {
    for (const rule of Array.from(rules)) {
      const type = rule.constructor.name
      if (type === 'CSSStyleRule') {
        try {
          takeRule(rule as CSSStyleRule)
        } catch {
          /* 개별 규칙 실패 skip */
        }
      } else if (type === 'CSSMediaRule') {
        try {
          const media = rule as CSSMediaRule
          if (win.matchMedia(media.conditionText).matches === true) {
            walk(media.cssRules)
          }
        } catch {
          /* skip */
        }
      }
    }
  }

  for (const sheet of Array.from(win.document.styleSheets)) {
    try {
      const owner = sheet.ownerNode as HTMLElement | null
      if (owner != null && skipMarker.length > 0 && owner.hasAttribute(skipMarker)) continue
      walk(sheet.cssRules)
    } catch {
      /* cross-origin 등 접근 불가 시트 skip */
    }
  }

  if (order.length === 0) return ''
  let css = ''
  for (const sel of order) {
    const decl = merged.get(sel)
    if (decl == null) continue
    let body = ''
    decl.forEach((info, prop) => {
      body += `${prop}:${info.value}${info.important ? ' !important' : ''};`
    })
    if (body.length > 0) css += `${sel}{${body}}\n`
  }
  return css
}

export interface StyleToInlineOptions {
  remSize?: number
  recursive?: boolean
  properties?: string[]
  clsPrefix?: string
  idPrefix?: string
  processElement: (element: Element) => void
  win?: Window
  shorthandCheck: null | ((element: Element, prop: string) => CheckResult)
  /** 지정 시 최상위 반환 요소에 의사요소(::before/::after) 보존용 `<style>` 을 주입한다(email id 기준 scope). */
  pseudoScopeId?: string
  /** 주입 `<style>` 식별 마커 속성명(기본 `data-omw-pseudo`). getSafeHTML 의 `<style>` 제거에서 skip 대상. */
  pseudoStyleMarker?: string
  /**
   * dir="auto" 처리 방식. dir="auto" 컨테이너는 자식 삽입마다 방향성(첫 강한 방향 문자) 재계산으로
   * 서브트리를 스캔해, 자식이 많은 본문의 클론 조립이 O(n²) 이 된다(24k 자식 실측: WebKit 4.0s /
   * Blink 1.1s → 양쪽 모두 두 모드에서 ~20ms). 두 모드 모두 이 비용을 제거하되 결과 의미가 다르다:
   * - 'resolve': 계산된 방향(ltr/rtl)으로 고정. 콘텐츠가 이후 변하지 않는 정적 소비처(뷰어) 전용.
   * - 'preserve'(기본): dir="auto" 를 유지하되, 조립 중에는 떼어 뒀다가 자식 완성 후(finalize)
   *   재부착해 재계산을 컨테이너당 1회로 줄인다. 편집기/서명 등 이후 내용이 수정되어 방향이
   *   자동으로 따라가야 하는 소비처용.
   * 어느 모드든 <textarea>/<pre> 는 dir=auto 가 행 단위 방향(unicode-bidi:plaintext) 의미를
   * 가지므로 건드리지 않는다.
   */
  dirAutoMode?: 'resolve' | 'preserve'
}

// preserve 모드에서 finalize 까지 dir="auto" 원값을 나르는 임시 마커 속성(SoT: ./markers).

interface StyleInfo {
  value: string
  isImportant: boolean
}

function mergeStyleInto(
  styleMap: Record<string, StyleInfo>,
  style: CSSStyleDeclaration,
  element: HTMLElement | SVGElement,
  options?: StyleToInlineOptions,
): void {
  for (let prop of Array.from(style)) {
    if (options?.properties == null || options.properties.includes(prop) === true) {
      let propName = prop
      let propValue: StyleInfo | null = null
      if (options?.shorthandCheck != null) {
        const checkResult = options.shorthandCheck(element, prop)
        if (checkResult != null) {
          propName = checkResult.prop
          propValue = {
            value: checkResult.value,
            isImportant: checkResult.isImportant,
          }
        }
      }

      if (propValue == null) {
        propValue = {
          value: style.getPropertyValue(prop),
          isImportant: style.getPropertyPriority(prop) === 'important',
        }
      }

      const prevStyleInfo = styleMap[propName]
      if (
        propValue.isImportant === true ||
        prevStyleInfo == null ||
        prevStyleInfo.isImportant === false
      ) {
        styleMap[propName] = propValue
      }
    }
  }
}

// 요소 1개의 인라인화 코어: 매칭 규칙 병합 → 얕은 클론에 style 속성 부여. 자식 처리/후처리
// (prefix·processElement)는 포함하지 않는다. 동기(재귀)·비동기(iterative) 두 구현이 공유한다.
function buildInlineElement(
  element: HTMLElement | SVGElement,
  options: StyleToInlineOptions | undefined,
  ruleMap: any,
): HTMLElement | SVGElement {
  // 낮은 우선순위의 스타일부터 적용하기 위해 역순으로 정렬
  const matches = matchRules(element, ruleMap).reverse()

  // we need to preserve any pre-existing inline styles.
  const newElement = cloneElem(element)

  // 입력 HTML 이 위조했을 수 있는 defer 마커 제거 — 마커는 이 변환이 직접 부여한 것만 유효하다.
  if (newElement.hasAttribute(DIR_DEFER_ATTR)) {
    newElement.removeAttribute(DIR_DEFER_ATTR)
  }

  // dir="auto" 처리(dirAutoMode 참고). 자식이 붙기 전(여기)에 치환/제거해야 조립 중 자식 삽입마다
  // 발생하는 방향성 재계산(O(n²))이 사라진다. :dir() 는 HTML 방향성(auto 해석 포함)을 매칭한다.
  const dirAttr = newElement.getAttribute('dir')
  if (
    dirAttr != null &&
    dirAttr.toLowerCase() === 'auto' &&
    element.tagName !== 'TEXTAREA' &&
    element.tagName !== 'PRE'
  ) {
    if (options?.dirAutoMode === 'resolve') {
      // 뷰어(정적 스냅샷): 계산된 방향으로 고정. 결과 DOM 의 후속 mutation 재계산 비용도 사라진다.
      try {
        newElement.setAttribute('dir', element.matches(':dir(rtl)') ? 'rtl' : 'ltr')
      } catch {
        /* :dir() 미지원 브라우저: dir="auto" 유지 */
      }
    } else {
      // preserve(기본): 조립 중에만 떼어 두고 finalize(자식 완성 후)에서 원값 그대로 재부착.
      newElement.removeAttribute('dir')
      newElement.setAttribute(DIR_DEFER_ATTR, dirAttr)
    }
  }

  const styleMap: Record<string, StyleInfo> = {}
  matches.forEach((rule) => {
    mergeStyleInto(styleMap, rule.style, element, options)
  })

  mergeStyleInto(styleMap, element.style, element, options)

  let styleStr = ''
  for (let prop in styleMap) {
    const { value, isImportant } = styleMap[prop]
    // @todo - 'html-react-parser'에서 important 처리가 안되는 문제가 있어서 important를 제거하고 처리함.
    // newElement.style.setProperty(prop, value, isImportant ? 'important' : '');
    let newValue = value
    if (options?.remSize != null && typeof value === 'string' && value.endsWith('rem') === true) {
      const remValue = getCSSValue(value, false, options.remSize)
      if (remValue.type == 1 && remValue.unit === 'px') {
        newValue = remValue.number + 'px'
      }
    }

    const importantStr = isImportant ? ' !important' : ''
    styleStr += `${prop}: ${newValue}${importantStr};`
  }

  if (styleStr.length > 0) {
    newElement.setAttribute('style', styleStr)
  }

  return newElement
}

// 자식 서브트리 완성 후(후위) 수행하는 요소 후처리: class/id prefix 부여 + processElement 콜백.
function finalizeInlineElement(
  newElement: HTMLElement | SVGElement,
  options?: StyleToInlineOptions,
): void {
  // preserve 모드의 dir="auto" 재부착(dirAutoMode 참고). 자식이 모두 붙은 뒤라 방향성 재계산이
  // 컨테이너당 1회로 끝난다. processElement 전에 수행해 외부 콜백에 임시 마커가 노출되지 않는다.
  const deferredDir = newElement.getAttribute(DIR_DEFER_ATTR)
  if (deferredDir != null) {
    newElement.removeAttribute(DIR_DEFER_ATTR)
    newElement.setAttribute('dir', deferredDir)
  }

  if (options?.clsPrefix && newElement.classList.length > 0) {
    const clsList = Array.from(newElement.classList).map((clsName) => {
      return options.clsPrefix + clsName
    })

    if (clsList.length > 0) {
      newElement.setAttribute('class', clsList.join(' '))
    }
  }

  const id = newElement.getAttribute('id')
  if (options?.idPrefix && id != null) {
    const newId = options.idPrefix + id

    newElement.setAttribute('id', newId)
  }

  if (options?.processElement) {
    options.processElement(newElement as Element)
  }
}

// 최상위 호출에서만: 의사요소(::before/::after) 규칙을 분리해 재작성한 <style> 를 반환 트리에 주입.
// styleToInlineStyle 은 계산된 스타일만 인라인화하므로 의사요소는 인라인 불가 → 별도 <style> 로 보존.
// 순수 additive — 실패 시 <style> 미주입(기존 동작 유지).
function injectPseudoStyle(
  newElement: HTMLElement | SVGElement,
  myWindow: Window,
  options?: StyleToInlineOptions,
): void {
  if (options?.pseudoScopeId == null || options.pseudoScopeId.length === 0) {
    return
  }
  try {
    const marker = options.pseudoStyleMarker ?? PSEUDO_STYLE_MARKER
    const css = collectPseudoElementCSS(
      myWindow,
      options.clsPrefix ?? '',
      options.idPrefix ?? '',
      marker,
    )
    if (css.length > 0) {
      const ownerDoc = newElement.ownerDocument
      if (ownerDoc != null) {
        // 컨테이너 스코프는 붙이지 않는다(클래스 한정 셀렉터라 오염 없음). 소비 컨텍스트에서 필요 시
        // prepend: 뷰어는 그대로(unscoped·idempotent), print 는 `.print-body` 를 추가한다.
        // 주입 <style> 는 marker 로만 식별(getSafeHTML 의 <style> 제거에서 skip 대상).
        const styleEl = ownerDoc.createElement('style')
        styleEl.setAttribute(marker, '')
        styleEl.textContent = css
        newElement.appendChild(styleEl)
      }
    }
  } catch {
    /* graceful degrade: 의사요소 <style> 미주입 */
  }
}

/**
 * A function that iterates through the computed style properties of a HTML
 * element and redefines them as inline styles.
 */
export default function styleToInlineStyle(
  element: HTMLElement | SVGElement,
  options?: StyleToInlineOptions,
  defaultRuleMap?: any,
): HTMLElement | SVGElement {
  if (!element) {
    throw new Error('No element specified.')
  }

  const myWindow = options?.win || window

  const ruleMap = defaultRuleMap || getRuleMap(myWindow)

  const newElement = buildInlineElement(element, options, ruleMap)

  if (options?.recursive) {
    Array.from(element.childNodes).forEach((node) => {
      let newNode = null
      if (node.nodeType === Node.ELEMENT_NODE) {
        newNode = styleToInlineStyle(node as HTMLElement, options, ruleMap)
      } else {
        newNode = node.cloneNode(false)
      }
      newElement.appendChild(newNode)
    })
  }

  finalizeInlineElement(newElement, options)

  if (defaultRuleMap == null) {
    deleteUniqueKeyName('styleToInline')
    injectPseudoStyle(newElement, myWindow, options)
  }

  return newElement
}

/**
 * 변환 일시정지 게이트. styleToInlineStyleAsync 가 yield 경계에서 `paused` 를 확인해, 닫혀 있으면
 * 열릴 때까지 대기한다 — 진행 중 작업은 **지연될 뿐 소멸하지 않는다**(promise pending 유지). 에디터
 * 탭 활성화 중 뷰어 bake 가 메인 스레드를 경합하지 않도록 host 가 열고 닫는다. abort 는 게이트 대기
 * 보다 우선한다(언마운트 시 즉시 AbortError). 구현: utils/core 의 BakePauseController.
 */
export interface BakePauseGate {
  readonly paused: boolean
  /** 게이트가 닫혀 있으면 열릴 때까지 resolve 를 미룬다. signal abort 시 AbortError 로 reject(abort 우선). */
  waitWhilePaused(signal?: AbortSignal): Promise<void>
}

export interface StyleToInlineAsyncOptions extends StyleToInlineOptions {
  /** 청크 사이 yield 시점에 중단 여부를 확인해, 중단됐으면 AbortError 로 reject 한다. */
  signal?: AbortSignal
  /** 청크 시간 예산(ms). 한 청크가 이 시간을 넘기면 메인 스레드에 양보한다. 기본 40. */
  yieldBudgetMs?: number
  /** 지정 시 yield 경계에서 일시정지 게이트를 확인해, 닫혀 있으면 열릴 때까지 변환을 미룬다(abort 우선). */
  pauseGate?: BakePauseGate
}

// scheduler.yield() 가용 시 그것을, 아니면 MessageChannel 매크로태스크로 메인 스레드에 양보한다.
// setTimeout(0) 폴백은 쓰지 않는다 — Safari(scheduler 미지원)는 중첩 타이머를 ~4ms+ 로 클램핑해
// yield 1회당 수 ms 를 낭비한다(24k 요소 메일에서 yield 125회 × ~10ms ≈ 1.2s 실측). MessageChannel
// 태스크는 클램핑이 없고(동일 측정 70배 빠름), 40ms 청크 사이 입력/렌더링 기회는 동일하게 생긴다
// (React scheduler 가 같은 이유로 쓰는 기법).
function yieldToMain(): Promise<void> {
  const sched = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler
  if (sched?.yield != null) {
    return sched.yield()
  }
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = () => {
      resolve()
    }
    channel.port2.postMessage(null)
  })
}

/**
 * styleToInlineStyle 의 비동기(iterative) 버전. 재귀 대신 명시적 스택으로 트리를 순회하며,
 * 청크 경과 시간이 yieldBudgetMs 를 넘으면 메인 스레드에 양보(yield)해 대형 메일 변환이
 * 입력 처리를 장시간 블록(freeze)하지 않게 한다. 출력(반환 트리·processElement 후위 순서)은
 * 동기 버전과 동일하다 — asyncEquivalence.test.ts 로 고정. 뷰어 경로(getSafeElement) 전용이며
 * 인쇄/서명/에디터가 쓰는 동기 styleToInlineStyle 은 변경하지 않는다.
 */
export async function styleToInlineStyleAsync(
  element: HTMLElement | SVGElement,
  options?: StyleToInlineAsyncOptions,
): Promise<HTMLElement | SVGElement> {
  if (!element) {
    throw new Error('No element specified.')
  }

  const myWindow = options?.win || window
  const ruleMap = getRuleMap(myWindow)
  const budgetMs = options?.yieldBudgetMs ?? 40

  // 재귀 버전과 동일한 순서 보장:
  // - enter: 요소를 클론·인라인화해 부모 클론에 즉시 append (형제 순서 = 문서 순서)
  // - exit: 자식 서브트리 완성 후 finalize(prefix·processElement) — 재귀의 후위 순서와 동일
  type Frame =
    | { kind: 'enter'; node: Node; parentClone: HTMLElement | SVGElement | null }
    | { kind: 'exit'; clone: HTMLElement | SVGElement }

  let rootClone: HTMLElement | SVGElement | null = null
  const stack: Frame[] = [{ kind: 'enter', node: element, parentClone: null }]
  let sliceStart = performance.now()

  while (stack.length > 0) {
    const frame = stack.pop()!
    if (frame.kind === 'exit') {
      finalizeInlineElement(frame.clone, options)
    } else if (frame.node.nodeType !== Node.ELEMENT_NODE) {
      frame.parentClone?.appendChild(frame.node.cloneNode(false))
    } else {
      const srcElement = frame.node as HTMLElement | SVGElement
      const clone = buildInlineElement(srcElement, options, ruleMap)
      if (frame.parentClone == null) {
        rootClone = clone
      } else {
        frame.parentClone.appendChild(clone)
      }
      stack.push({ kind: 'exit', clone })
      if (options?.recursive) {
        const children = srcElement.childNodes
        for (let i = children.length - 1; i >= 0; i--) {
          stack.push({ kind: 'enter', node: children[i], parentClone: clone })
        }
      }
    }

    if (performance.now() - sliceStart > budgetMs) {
      await yieldToMain()
      // abort 는 pause 보다 우선 — 대기 진입 전에 먼저 확인한다.
      if (options?.signal?.aborted === true) {
        deleteUniqueKeyName('styleToInline')
        throw new DOMException('Aborted', 'AbortError')
      }
      // 일시정지 게이트가 닫혀 있으면 열릴 때까지 대기(promise pending 유지). 대기 중 signal abort
      // 가 오면 waitWhilePaused 가 AbortError 로 reject 한다(abort 우선). 작업은 지연될 뿐 소멸하지
      // 않으며, 재개되면 이어서 변환한다.
      const gate = options?.pauseGate
      if (gate != null && gate.paused === true) {
        try {
          await gate.waitWhilePaused(options?.signal)
        } catch (err) {
          deleteUniqueKeyName('styleToInline')
          throw err
        }
      }
      sliceStart = performance.now()
    }
  }

  deleteUniqueKeyName('styleToInline')
  if (rootClone != null) {
    injectPseudoStyle(rootClone, myWindow, options)
  }

  return rootClone!
}
